import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { templatesApi } from '@/features/DataHub/api/templates.api';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { useSelectedSymbols } from '@/features/DataHub/hooks/useSelectedSymbols';
import { templateKeys } from '@/features/DataHub/queries/keys/templates.keys';
import useTokenGeneration from '@/pages/Profile/TokenGeneration/useTokenGeneration';

import useCodePreview from './useCodePreview';
import useDownloadFileContent from './useDownloadFileContent';
import { useExportSettingsContext } from './useExportSettingsContext';
import { pythonPreviewService } from '../services';

const usePythonPreview = (capturePosthogEvent: () => void, enabled = true) => {
  const { exportType, includeMetadata, from, to, period, periodNumber } =
    useExportSettingsContext();
  const symbols = useSelectedSymbols();

  const { apiKeys } = useTokenGeneration();
  const sortedKeys = [...apiKeys]?.sort(
    (a, b) => new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime()
  );
  const apiKey = sortedKeys?.at(-1)?.prefixedRawApiKey ?? '<YOUR_GX_API_TOKEN>';

  const { timezoneValue } = useDataHubUserConfig();
  const userTimezone = timezoneValue;
  const { createRequestToValue, createRequestFromValue } = useCodePreview(
    exportType,
    from,
    to,
    period,
    periodNumber
  );

  const { downloadFileContent } = useDownloadFileContent(capturePosthogEvent);

  const {
    data: pythonScriptTemplate = '',
    isLoading: loading,
    isError: error
  } = useQuery({
    queryKey: templateKeys.detail('python'),
    enabled,
    queryFn: () => templatesApi.fetchPythonTemplate()
  });

  const fileContent = useMemo(() => {
    if (!enabled || !pythonScriptTemplate) return '';

    const toValue = createRequestToValue();
    const fromValue = createRequestFromValue();

    // Use the service to generate preview
    const result = pythonPreviewService.generateFromTemplate(pythonScriptTemplate, {
      symbols,
      rangeStart: fromValue,
      rangeEnd: toValue,
      timezone: userTimezone,
      includeMetadata,
      apiKey,
      gxURL: '' // Not needed for Python preview
    });

    return result.content;
  }, [
    enabled,
    pythonScriptTemplate,
    symbols,
    from,
    to,
    includeMetadata,
    period,
    periodNumber,
    exportType,
    createRequestToValue,
    createRequestFromValue,
    userTimezone,
    apiKey
  ]);

  const downloadFile = () => {
    downloadFileContent(fileContent, 'python');
  };

  return { loading, error, fileContent, downloadFile };
};

export default usePythonPreview;
